const $$splitComponentImporter = () => import('/app/src/routes/{-$locale}/sellers/$id.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
import { createFileRoute } from "@tanstack/react-router";
import { dehydrate } from "@tanstack/react-query";
import { httpClient } from "@/lib/http-client";
import { getSeller } from "@/queries/seller";
import { getQueryClient } from "@/lib/client";
import { getFetchedQuery } from "@/lib/get-fetched-query";
import i18n from "@/lib/i18n";
import { formSchema } from "@/stores/searchStore";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
interface SellerStats {
  offers: number;
  items: number;
  games: number;
  freegames: number;
  seller: {
    _id: string;
    name: string;
    igdb_id?: number;
    logo?: {
      _id: string;
      url: string;
      height: number;
      width: number;
      checksum: string;
      animated: boolean;
      alpha_channel: boolean;
    };
    createdAt?: string;
    updatedAt?: string;
  };
}
export const Route = createFileRoute("/{-$locale}/sellers/$id")({
  component: lazyRouteComponent($$splitComponentImporter, 'component'),
  loader: async ({
    context,
    params
  }) => {
    const {
      queryClient,
      country
    } = context;
    const {
      id
    } = params;
    await Promise.allSettled([queryClient.prefetchQuery({
      queryKey: ["seller", {
        id,
        country
      }],
      queryFn: async () => getSeller(id, country ?? "US").catch(() => [])
    }), queryClient.prefetchQuery({
      queryKey: ["seller:cover", {
        id,
        country
      }],
      queryFn: async () => httpClient.get<SingleOffer[]>(`/sellers/${id}/cover`, {
        params: {
          country
        }
      }).catch(() => [])
    }), queryClient.prefetchQuery({
      queryKey: ["seller:stats", {
        id
      }],
      queryFn: async () => httpClient.get<SellerStats>(`/sellers/${id}/stats`).catch(() => null)
    })]);
    return {
      id,
      dehydratedState: dehydrate(queryClient),
      country
    };
  },
  validateSearch: zodSearchValidator(formSchema),
  head: ctx => {
    const {
      params,
      loaderData
    } = ctx;
    const queryClient = getQueryClient();
    if (!loaderData) {
      return {
        meta: [{
          title: i18n.t("sellers.meta.notFoundTitle"),
          description: i18n.t("sellers.meta.notFoundDescription")
        }]
      };
    }
    const seller = getFetchedQuery<SingleOffer[]>(queryClient, loaderData?.dehydratedState, ["seller", {
      id: params.id,
      country: loaderData.country
    }]);
    const stats = getFetchedQuery<SellerStats | null>(queryClient, loaderData?.dehydratedState, ["seller:stats", {
      id: params.id
    }]);
    const sellerName = seller?.[0]?.seller?.name ?? stats?.seller.name;
    if (!sellerName) return {
      meta: [{
        title: i18n.t("sellers.meta.notFoundTitle"),
        description: i18n.t("sellers.meta.notFoundDescription")
      }]
    };
    return {
      meta: [{
        title: i18n.t("sellers.meta.title", {
          name: sellerName
        })
      }]
    };
  }
});